"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { ArrowUpLeft } from "lucide-react";
import { FrameCorners } from "@/features/collections/detail/vitrine/Ornaments";
import { Acts, BidAction, Clock, Figures, Meta, Phase, PriceNow, RangeScale, Seller, TimeBar, When, tr, type Ctx } from "./parts";
import { Book } from "./Sections";
import SettlementNotice from "../SettlementNotice";

/** The art, framed: a gilt key-line and the vitrine's لچک corners. Always the page's LCP. */
export function Framed(t0) {
  const $ = _c(8);
  const {
    image,
    name,
    sizes,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `ap-frame ${className}`;
  let t3;
  if ($[0] !== image || $[1] !== name || $[2] !== sizes) {
    t3 = <span className="ap-canvas"><Image src={image} alt={name} fill={true} preload={true} fetchPriority="high" sizes={sizes} /></span>;
    $[0] = image;
    $[1] = name;
    $[2] = sizes;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FrameCorners />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t3) {
    t5 = <figure className={t2}>{t3}{t4}</figure>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

/**
 * ۱ — پیشخوان. The work in its frame on one side, held still while the page
 * scrolls; on the other the counter where the bid is made: what it is, the
 * clock as four boxes on a thin line of elapsed time, the price, the range,
 * three figures and the action — every decision on one card.
 */
export function D1Counter(c) {
  const $ = _c(89);
  const {
    auction,
    p,
    lang,
    name,
    workName,
    workHref,
    image
  } = c;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== auction || $[1] !== image || $[2] !== lang || $[3] !== name || $[4] !== p || $[5] !== workHref || $[6] !== workName) {
    const t = tr(lang);
    t4 = "d1";
    const t7 = `url("${image}")`;
    let t8;
    if ($[14] !== t7) {
      t8 = {
        "--ap-art": t7
      };
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t5 = t8 as React.CSSProperties;
    if ($[16] !== image || $[17] !== name) {
      t6 = <div className="d1-art"><Framed image={image} name={name} sizes="(max-width: 1023px) 92vw, 46vw" /></div>;
      $[16] = image;
      $[17] = name;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t2 = "d1-desk";
    let t9;
    if ($[19] !== auction || $[20] !== lang) {
      t9 = <Meta auction={auction} lang={lang} />;
      $[19] = auction;
      $[20] = lang;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== name) {
      t10 = <h1>{name}</h1>;
      $[22] = name;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    const t11 = workName ? <p className="ap-work">{auction.model_type === "collection" ? t("\u06A9\u0627\u0644\u06A9\u0634\u0646", "Collection") : t("\u0627\u062B\u0631", "Artwork")}:{" "}{workHref ? <Link href={workHref} className="v2-focus"><bdi>{workName}</bdi><ArrowUpLeft aria-hidden={true} /></Link> : <bdi>{workName}</bdi>}</p> : null;
    let t12;
    if ($[24] !== auction || $[25] !== lang) {
      t12 = <Seller auction={auction} lang={lang} />;
      $[24] = auction;
      $[25] = lang;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    let t13;
    if ($[27] !== lang || $[28] !== name || $[29] !== p) {
      t13 = <Acts p={p} lang={lang} name={name} />;
      $[27] = lang;
      $[28] = name;
      $[29] = p;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== t12 || $[32] !== t13) {
      t14 = <div className="d1-row">{t12}{t13}</div>;
      $[31] = t12;
      $[32] = t13;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    if ($[34] !== t10 || $[35] !== t11 || $[36] !== t14 || $[37] !== t9) {
      t3 = <header className="d1-head">{t9}{t10}{t11}{t14}</header>;
      $[34] = t10;
      $[35] = t11;
      $[36] = t14;
      $[37] = t9;
      $[38] = t3;
    } else {
      t3 = $[38];
    }
    t0 = "d1-card";
    t1 = t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F", "Bidding");
    $[0] = auction;
    $[1] = image;
    $[2] = lang;
    $[3] = name;
    $[4] = p;
    $[5] = workHref;
    $[6] = workName;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t0 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[39] !== lang || $[40] !== p) {
    t7 = <Phase p={p} lang={lang} />;
    $[39] = lang;
    $[40] = p;
    $[41] = t7;
  } else {
    t7 = $[41];
  }
  let t8;
  if ($[42] !== auction || $[43] !== lang || $[44] !== p) {
    t8 = <When auction={auction} p={p} lang={lang} />;
    $[42] = auction;
    $[43] = lang;
    $[44] = p;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  let t9;
  if ($[46] !== t7 || $[47] !== t8) {
    t9 = <div className="d1-card-top">{t7}{t8}</div>;
    $[46] = t7;
    $[47] = t8;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== lang || $[50] !== p) {
    t10 = <Clock p={p} lang={lang} boxes={true} />;
    $[49] = lang;
    $[50] = p;
    $[51] = t10;
  } else {
    t10 = $[51];
  }
  let t11;
  if ($[52] !== p) {
    t11 = <TimeBar p={p} />;
    $[52] = p;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[54] !== auction || $[55] !== lang || $[56] !== p) {
    t12 = <PriceNow auction={auction} p={p} lang={lang} />;
    t13 = <RangeScale auction={auction} p={p} lang={lang} />;
    t14 = <Figures auction={auction} p={p} lang={lang} />;
    t15 = <SettlementNotice auction={auction} p={p} lang={lang} />;
    $[54] = auction;
    $[55] = lang;
    $[56] = p;
    $[57] = t12;
    $[58] = t13;
    $[59] = t14;
    $[60] = t15;
  } else {
    t12 = $[57];
    t13 = $[58];
    t14 = $[59];
    t15 = $[60];
  }
  let t16;
  if ($[61] !== lang || $[62] !== p) {
    t16 = <BidAction p={p} lang={lang} />;
    $[61] = lang;
    $[62] = p;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== t0 || $[65] !== t1 || $[66] !== t10 || $[67] !== t11 || $[68] !== t12 || $[69] !== t13 || $[70] !== t14 || $[71] !== t15 || $[72] !== t16 || $[73] !== t9) {
    t17 = <section className={t0} aria-label={t1}>{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}</section>;
    $[64] = t0;
    $[65] = t1;
    $[66] = t10;
    $[67] = t11;
    $[68] = t12;
    $[69] = t13;
    $[70] = t14;
    $[71] = t15;
    $[72] = t16;
    $[73] = t9;
    $[74] = t17;
  } else {
    t17 = $[74];
  }
  let t18;
  if ($[75] !== t17 || $[76] !== t2 || $[77] !== t3) {
    t18 = <div className={t2}>{t3}{t17}</div>;
    $[75] = t17;
    $[76] = t2;
    $[77] = t3;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== t18 || $[80] !== t4 || $[81] !== t5 || $[82] !== t6) {
    t19 = <div className={t4} style={t5}>{t6}{t18}</div>;
    $[79] = t18;
    $[80] = t4;
    $[81] = t5;
    $[82] = t6;
    $[83] = t19;
  } else {
    t19 = $[83];
  }
  let t20;
  if ($[84] !== c) {
    t20 = <Book {...c} />;
    $[84] = c;
    $[85] = t20;
  } else {
    t20 = $[85];
  }
  let t21;
  if ($[86] !== t19 || $[87] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[86] = t19;
    $[87] = t20;
    $[88] = t21;
  } else {
    t21 = $[88];
  }
  return t21;
}